import {
  AlertVariants,
  Button,
  ButtonVariants,
  Flex,
  FormControlSizes,
  HStack,
  Icon,
  IconName,
  Kbd,
  TabbedDrawerHeader,
  TabIconButton,
  useOnKeyDown,
} from '@talos/kyoko';
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type FormEvent } from 'react';
import { useAvaAI } from '../../providers/AvaAIContext';
import { AvaAIErrorCode, getAvaAIErrorCode, getAvaAIErrorMessage } from '../../utils/avaAI';
import { AvaAIMarkdown } from './AvaAIMarkdown';
import {
  AvaAIBody,
  AvaAIComposer,
  AvaAIContentWrapper,
  AvaAIError,
  AvaAIFooter,
  AvaAIGlobalStyles,
  AvaAIInput,
  AvaAIMessage,
  AvaAIMessagePlaceholder,
  AvaAIMessages,
  AvaAISendButton,
  AvaAIWelcome,
} from './styles';
import { AVA_AI_COMPOSER_INPUT_ID, AVA_AI_DISPLAY_NAME } from './tokens';

const ESCAPE_KEY = { key: 'Escape' };

interface AvaAIContentProps {
  onClose: () => void;
  onPopout?: () => void;
}

export function AvaAIContent({ onClose, onPopout }: AvaAIContentProps) {
  const {
    messages,
    status,
    error,
    sendMessage,
    stop,
    regenerate,
    clearError,
    reset,
    isOpen,
    isPopped,
    composerDraft,
    setComposerDraft,
    composerFocusRequest,
  } = useAvaAI();
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const handledComposerFocusRequestRef = useRef(0);
  const contentRef = useRef<HTMLElement>(null);
  const messagesRef = useRef<HTMLDivElement>(null);
  const footerRef = useRef<HTMLDivElement>(null);
  const shouldStickToBottomRef = useRef(true);
  const [footerHeight, setFooterHeight] = useState(0);
  const isBusy = status === 'submitted' || status === 'streaming';
  const lastMessage = messages.at(-1);
  const lastMessageHasAssistantText =
    lastMessage?.role === 'assistant' &&
    lastMessage.parts.some(part => part.type === 'text' && part.text.trim() !== '');
  const showIncomingPlaceholder = isBusy && !lastMessageHasAssistantText;
  const hasInput = composerDraft.trim().length > 0;
  const isSessionLimitError = getAvaAIErrorCode(error) === AvaAIErrorCode.SessionLimitExceeded;

  useEffect(() => {
    if (!isOpen || !isPopped) {
      return;
    }

    const popoutWindow = inputRef.current?.ownerDocument.defaultView;
    if (popoutWindow == null || popoutWindow === window) {
      return;
    }

    const focusInput = () => {
      const input = inputRef.current;
      if (input == null || input.ownerDocument.activeElement === input) {
        return;
      }
      input.focus();
    };

    // The window can already be focused by the time this content mounts.
    if (popoutWindow.document.hasFocus()) {
      focusInput();
    }

    popoutWindow.addEventListener('focus', focusInput);
    return () => popoutWindow.removeEventListener('focus', focusInput);
  }, [isOpen, isPopped]);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const explicitFocusRequest = composerFocusRequest > handledComposerFocusRequestRef.current;
    if (isPopped && !explicitFocusRequest) {
      return;
    }

    handledComposerFocusRequestRef.current = composerFocusRequest;

    const input = inputRef.current;
    if (input == null) {
      return;
    }
    if (isPopped) {
      input.ownerDocument.defaultView?.focus();
    }
    input.focus();
  }, [composerFocusRequest, isOpen, isPopped]);

  useLayoutEffect(() => {
    const inputElement = inputRef.current;
    if (inputElement == null) {
      return;
    }

    inputElement.style.height = 'auto';
    inputElement.style.height = `${inputElement.scrollHeight}px`;
  }, [composerDraft, isOpen]);

  useLayoutEffect(() => {
    const footerElement = footerRef.current;
    if (footerElement == null) {
      return;
    }

    const updateFooterHeight = () => {
      const nextHeight = footerElement.getBoundingClientRect().height;
      setFooterHeight(currentHeight => (currentHeight === nextHeight ? currentHeight : nextHeight));
    };

    updateFooterHeight();
    const resizeObserver = new ResizeObserver(() => {
      window.requestAnimationFrame(updateFooterHeight);
    });
    resizeObserver.observe(footerElement);
    return () => resizeObserver.disconnect();
  }, []);

  useLayoutEffect(() => {
    const messagesElement = messagesRef.current;
    if (messagesElement == null || !shouldStickToBottomRef.current) {
      return;
    }

    messagesElement.scrollTop = messagesElement.scrollHeight;
  }, [footerHeight, messages, showIncomingPlaceholder]);

  useOnKeyDown(ESCAPE_KEY, isOpen ? onClose : undefined, contentRef);

  const handleMessagesScroll = useCallback(() => {
    const messagesElement = messagesRef.current;
    if (messagesElement == null) {
      return;
    }

    shouldStickToBottomRef.current =
      messagesElement.scrollHeight - messagesElement.scrollTop - messagesElement.clientHeight <= 1;
  }, []);

  const handleSend = useCallback(() => {
    const text = composerDraft.trim();
    if (isBusy || text.length === 0) {
      return;
    }

    shouldStickToBottomRef.current = true;
    void sendMessage(text).finally(() => {
      inputRef.current?.focus();
    });
  }, [composerDraft, isBusy, sendMessage]);

  const handleSubmit = useCallback(
    (event: FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      handleSend();
    },
    [handleSend]
  );

  const handleRetry = useCallback(() => {
    clearError();
    void regenerate();
  }, [clearError, regenerate]);

  const headerActions = (
    <>
      {onPopout != null && (
        <TabIconButton
          icon={IconName.ExternalLink}
          aria-label="Pop out chat"
          title="Pop out chat"
          data-testid="ava-ai-popout"
          onClick={onPopout}
        />
      )}
      <TabIconButton
        icon={IconName.Refresh}
        aria-label="Reset chat"
        title="Reset chat"
        data-testid="ava-ai-reset"
        onClick={reset}
      />
    </>
  );

  return (
    <AvaAIContentWrapper ref={contentRef} aria-label={AVA_AI_DISPLAY_NAME} data-testid="ava-ai-overlay">
      <AvaAIGlobalStyles />
      <TabbedDrawerHeader
        title={AVA_AI_DISPLAY_NAME}
        actions={headerActions}
        onClose={onClose}
        closeButtonTestId="close-dialog-button"
        aria-label={`${AVA_AI_DISPLAY_NAME} header`}
      />

      <AvaAIBody $footerHeight={footerHeight}>
        <AvaAIMessages ref={messagesRef} aria-live="polite" role="log" onScroll={handleMessagesScroll}>
          {messages.length === 0 && !showIncomingPlaceholder ? (
            <AvaAIWelcome data-testid="ava-ai-welcome">How can I help?</AvaAIWelcome>
          ) : (
            <>
              {messages.map(message => {
                const textParts = message.parts.filter(part => part.type === 'text' && part.text.trim() !== '');
                if (textParts.length === 0) {
                  return null;
                }

                return (
                  <AvaAIMessage key={message.id} $isUser={message.role === 'user'} data-testid="ava-ai-message">
                    {textParts.map((part, index) =>
                      part.type === 'text' ? <AvaAIMarkdown key={`${message.id}-${index}`} text={part.text} /> : null
                    )}
                  </AvaAIMessage>
                );
              })}
              {showIncomingPlaceholder && (
                <AvaAIMessagePlaceholder
                  role="status"
                  aria-label={`Waiting for ${AVA_AI_DISPLAY_NAME}`}
                  data-testid="ava-ai-message-placeholder"
                />
              )}
            </>
          )}
        </AvaAIMessages>

        <AvaAIFooter ref={footerRef}>
          {error && (
            <AvaAIError role="alert" variant={AlertVariants.Negative} dismissable={false} showIcon>
              <span>{getAvaAIErrorMessage(error)}</span>
              <Button
                type="button"
                variant={ButtonVariants.Negative}
                size={FormControlSizes.Small}
                ghost
                data-testid={isSessionLimitError ? 'ava-ai-new-session' : 'ava-ai-retry'}
                onClick={isSessionLimitError ? reset : handleRetry}
              >
                {isSessionLimitError ? 'Start new session' : 'Retry'}
              </Button>
            </AvaAIError>
          )}

          <AvaAIComposer onSubmit={handleSubmit} aria-busy={isBusy} data-testid="ava-ai-composer">
            <AvaAIInput
              id={AVA_AI_COMPOSER_INPUT_ID}
              aria-label={`Message ${AVA_AI_DISPLAY_NAME}`}
              data-testid="ava-ai-input"
              placeholder={`Ask ${AVA_AI_DISPLAY_NAME}`}
              value={composerDraft}
              ref={inputRef}
              onChange={event => setComposerDraft(event.target.value)}
              onEnter={handleSend}
              readOnly={isBusy}
              rows={1}
            />
            <Flex justifyContent="flex-end" p="spacing8">
              <AvaAISendButton
                type={isBusy ? 'button' : 'submit'}
                variant={ButtonVariants.Primary}
                ghost={!isBusy && !hasInput}
                onClick={isBusy ? stop : undefined}
                aria-label={isBusy ? 'Stop' : undefined}
                data-testid="ava-ai-send"
                disabled={!isBusy && !hasInput}
              >
                <HStack gap="spacing4" m="-spacing4">
                  {isBusy ? '' : 'Send'}
                  <Kbd>
                    <Icon icon={isBusy ? IconName.Stop : IconName.Enter} />
                  </Kbd>
                </HStack>
              </AvaAISendButton>
            </Flex>
          </AvaAIComposer>
        </AvaAIFooter>
      </AvaAIBody>
    </AvaAIContentWrapper>
  );
}
